import { Box, CircularProgress, Grid, MenuItem, Select, TextField } from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import { Button } from 'components/facets'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import { useUser } from 'components/providers/UserProvider'
import exportImg from 'img/all-exports.png'
import {
  createTransactionCsvReport,
  downloadTransactionCsvReport,
  fetchTransactionCsvReports,
  getStatusTransactionCsvReport,
} from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table, TableRowRenderer } from 'react-virtualized'
import {
  ChannelFilterOptions,
  CsvReportHistory,
  formatDateToYYYYMMDD,
  getNDaysAgo,
  normalizeCsvReportHistory,
  parseLocalDate,
} from 'shared'
import './exports-presenter.css'

// same limit Rails enforces on the interval
const MAX_INTERVAL_DAYS = 30

export const EXPORT_TYPE_LABELS: Record<string, string> = {
  quantity: 'transaction.quantityTransactions',
  price: 'transaction.priceTransactions',
  reservation: 'transaction.reservationTransactions',
  tax: 'transaction.taxTransactions',
}

export const TransactionsExports = (): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useUser()
  const [from, setFrom] = useState<Date>(getNDaysAgo(MAX_INTERVAL_DAYS))
  const [to, setTo] = useState<Date>(new Date())
  const [channel, setChannel] = useState<string>(ChannelFilterOptions.powertools)
  const [exportType, setExportType] = useState<string>('quantity')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLastPage, setIsLastPage] = useState<boolean>(true)

  const [downloads, setDownloads] = useState<CsvReportHistory[]>([])
  const [loadingReports, setLoadingReports] = useState(true)
  const [filterMonth, setFilterMonth] = useState<Date | null>(null)
  const [hasRecords, setHasRecords] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  const today = new Date()
  const todayStr = formatDateToYYYYMMDD(today)

  // Tax transactions exist only on powertools, and only for users with taxation enabled
  const exportTypes = Object.entries(EXPORT_TYPE_LABELS).filter(
    ([type]) =>
      type !== 'tax' ||
      (channel === ChannelFilterOptions.powertools && !!user?.featureFlags?.taxation)
  )

  const handleChannelChange = (newChannel: string) => {
    setChannel(newChannel)
    // tax transactions exist only on powertools
    if (newChannel !== ChannelFilterOptions.powertools && exportType === 'tax') {
      setExportType('quantity')
    }
  }

  const handleCreateCsv = async () => {
    if (from > today || to > today) {
      enqueueSnackbar(t('exports.errors.errorFutureDate'), { variant: 'error' })
      return
    }
    // dates are local midnights: round away daylight saving shifts
    const days = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
    if (days < 0) {
      enqueueSnackbar(t('transaction.invalidNegativeTimeRange'), { variant: 'error' })
      return
    }
    if (days > MAX_INTERVAL_DAYS) {
      enqueueSnackbar(t('exports.errors.errorIntervalTooLong'), { variant: 'error' })
      return
    }
    setIsCreating(true)
    try {
      const exportJob = await createTransactionCsvReport(
        formatDateToYYYYMMDD(from),
        formatDateToYYYYMMDD(to),
        channel,
        exportType
      )
      const normalize = normalizeCsvReportHistory(exportJob)

      pollExportStatus(normalize.id)

      setDownloads((prev) => [normalize, ...prev])
      setHasRecords(true)
    } catch {
      enqueueSnackbar(t('exports.errors.errorCreateTransactionsExport'), { variant: 'error' })
    } finally {
      setIsCreating(false)
    }
  }

  const handleFetchReports = async (overrideMonth?: Date | null) => {
    setLoadingReports(true)
    const month = overrideMonth !== undefined ? overrideMonth : filterMonth
    try {
      let createdAtFrom: string | undefined
      let createdAtTo: string | undefined
      if (month) {
        const year = month.getFullYear()
        const m = month.getMonth() + 1
        const monthStr = `${year}-${String(m).padStart(2, '0')}`
        createdAtFrom = `${monthStr}-01`
        const lastDay = new Date(year, m, 0).getDate()
        createdAtTo = `${monthStr}-${String(lastDay).padStart(2, '0')}`
      }
      const { files, hasNextPage } = await fetchTransactionCsvReports(
        currentPage,
        createdAtFrom,
        createdAtTo
      )
      if (files) {
        setDownloads(files)
        setIsLastPage(!hasNextPage)
        if (!month && files.length > 0) setHasRecords(true)
      } else {
        enqueueSnackbar(t('exports.errors.errorFetchReports'), { variant: 'error' })
      }
    } finally {
      setLoadingReports(false)
    }
  }

  const pollExportStatus = async (id: number) => {
    let res
    try {
      res = await getStatusTransactionCsvReport(id)
    } catch {
      enqueueSnackbar(t('exports.errors.errorCheckExportStatus'), { variant: 'error' })
      return
    }

    if (res.status === 'ready') {
      setDownloads((prev) =>
        prev.map((exp) => (exp.id === id ? { ...exp, exportEndedAt: new Date() } : exp))
      )
      enqueueSnackbar(t('exports.csvReady'), { variant: 'success' })
      handleFetchReports()
      return
    }

    if (res.status === 'pending') {
      setTimeout(() => pollExportStatus(id), 5000)
      return
    }

    enqueueSnackbar(t('exports.errors.errorCheckExportStatus'), { variant: 'error' })
  }

  useEffect(() => {
    handleFetchReports()
  }, [currentPage])

  const handleApplyFilters = () => {
    if (currentPage === 1) {
      handleFetchReports()
    } else {
      setCurrentPage(1)
    }
  }

  const handleResetFilter = () => {
    setFilterMonth(null)
    if (currentPage === 1) {
      handleFetchReports(null)
    } else {
      setCurrentPage(1)
    }
  }

  const handleDownloadCsv = async (id: number, params: CsvReportHistory['params']) => {
    let res
    try {
      res = await downloadTransactionCsvReport(id)
    } catch {
      enqueueSnackbar(t('exports.errors.errorDownloadCsv'), { variant: 'error' })
      return
    }

    const blob = new Blob([res.data], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transactions-report-${params?.channel}-${params?.exportType}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const rowRenderer: TableRowRenderer = ({ key, style, columns, className }) => (
    <div key={key} style={style} className={`${className}`}>
      {columns}
    </div>
  )

  const exportForm = (
    <Grid
      container
      spacing={2}
      alignItems="center"
      justifyContent={hasRecords ? undefined : 'center'}
    >
      <Grid item xs={6} sm={3} md={hasRecords ? 2 : 3}>
        <TextField
          fullWidth
          label={t('orders.from')}
          type="date"
          defaultValue={formatDateToYYYYMMDD(from)}
          onChange={(e) => setFrom(parseLocalDate(e.target.value))}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: todayStr }}
        />
      </Grid>
      <Grid item xs={6} sm={3} md={hasRecords ? 2 : 3}>
        <TextField
          fullWidth
          label={t('orders.to')}
          type="date"
          defaultValue={formatDateToYYYYMMDD(to)}
          onChange={(e) => setTo(parseLocalDate(e.target.value))}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: todayStr }}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={3}
        md={hasRecords ? 2 : 3}
        className="mt-3"
        data-testid="transactions-export-channel"
      >
        <Select
          fullWidth
          value={channel}
          onChange={(e) => handleChannelChange(e.target.value as string)}
        >
          {Object.values(ChannelFilterOptions).map((c) => (
            <MenuItem key={c} value={c}>
              {t(`common.channels.${c}`)}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid
        item
        xs={12}
        sm={3}
        md={hasRecords ? 2 : 3}
        className="mt-3"
        data-testid="transactions-export-type"
      >
        <Select
          fullWidth
          value={exportType}
          onChange={(e) => setExportType(e.target.value as string)}
        >
          {exportTypes.map(([type, label]) => (
            <MenuItem key={type} value={type}>
              {t(label)}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12} sm="auto" className="mt-3">
        <Button variant="contained" onClick={handleCreateCsv} disabled={isCreating}>
          {isCreating ? <CircularProgress size={20} /> : t('csv.export')}
        </Button>
      </Grid>
    </Grid>
  )

  return (
    <div className="tab-page-content">
      {loadingReports ? (
        <div className="w-100 d-flex flex-column align-items-center mt-5">
          <CircularProgress size={60} />
        </div>
      ) : !hasRecords ? (
        <div className="w-100 d-flex flex-column align-items-center mt-3">
          <img src={exportImg} style={{ maxWidth: 600, width: '100%', height: 'auto' }} />
          <Box maxWidth={800} width="100%" px={2} className="text-center">
            <h2 className="fst-italic mt-3 text-center">{t('exports.transactionsDescription')}</h2>
            <div className="mt-3">{exportForm}</div>
          </Box>
        </div>
      ) : (
        <>
          <div>
            <h2 className="mb-4">{t('exports.transactionsTitle')}</h2>
            {exportForm}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap mt-4 mb-3">
            <span>{t('exports.showExportsCovering')}</span>
            <DatePicker
              views={['year', 'month']}
              openTo="month"
              format="MM/yyyy"
              variant="inline"
              autoOk
              className="muiDatePicker"
              value={filterMonth}
              onChange={(date) => setFilterMonth(date)}
              maxDate={today}
              inputProps={{ style: { padding: '4px 8px', fontSize: 13 } }}
              style={{ width: 170 }}
              PopoverProps={{ classes: { paper: 'exports-month-picker' } }}
            />
            <Button variant="outlined" onClick={handleApplyFilters}>
              {t('exports.applyFilter')}
            </Button>
            {filterMonth && (
              <Button variant="outlined" onClick={handleResetFilter}>
                {t('exports.resetFilter')}
              </Button>
            )}
          </div>

          <div>
            <div className="responsive-table-container">
              <AutoSizer disableHeight>
                {({ width: autoWidth }) => {
                  const width = Math.max(autoWidth, 900)
                  return (
                    <Table
                      width={width}
                      height={40 + Math.max(40, downloads.length * 40)}
                      headerHeight={40}
                      rowHeight={40}
                      rowCount={downloads.length}
                      rowGetter={({ index }) => downloads[index]}
                      rowRenderer={rowRenderer}
                      overscanRowCount={5}
                    >
                      <Column
                        width={width * 0.15}
                        label={t('exports.fromDate')}
                        dataKey="from"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">
                            {new Date(a.intervalStartDate).toLocaleDateString()}
                          </div>
                        )}
                      />
                      <Column
                        width={width * 0.15}
                        label={t('exports.toDate')}
                        dataKey="to"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">
                            {new Date(a.intervalEndDate).toLocaleDateString()}
                          </div>
                        )}
                      />
                      <Column
                        width={width * 0.15}
                        label={t('exports.createdAt')}
                        dataKey="created"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">{new Date(a.createdAt).toLocaleString()}</div>
                        )}
                      />
                      <Column
                        width={width * 0.18}
                        label={t('exports.exportType')}
                        dataKey="exportType"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">
                            {t(EXPORT_TYPE_LABELS[a.params?.exportType] ?? '—')}
                          </div>
                        )}
                      />
                      <Column
                        width={width * 0.13}
                        label={t('transaction.channel')}
                        dataKey="channel"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">
                            {t(`common.channels.${a.params?.channel}`)}
                          </div>
                        )}
                      />
                      <Column
                        width={width * 0.08}
                        label={t('exports.status')}
                        dataKey="status"
                        headerClassName="centered-header"
                        cellRenderer={({ rowData: a }) => (
                          <Box textAlign={'center'}>
                            {a.lastErrorAt ? (
                              <i className="fa-light fa-circle-x error-color" />
                            ) : a.exportEndedAt ? (
                              <i className="fa-light fa-circle-check" style={{ color: 'green' }} />
                            ) : (
                              <CircularProgress size={16} />
                            )}
                          </Box>
                        )}
                      />
                      <Column
                        width={width * 0.08}
                        label={t('exports.download')}
                        dataKey="download"
                        headerClassName="centered-header"
                        cellRenderer={({ rowData: a }) => (
                          <Box textAlign={'center'}>
                            {!a.lastErrorAt && a.exportEndedAt ? (
                              <i
                                className="fa-light fa-arrow-down-to-bracket table-action-icon"
                                onClick={() => handleDownloadCsv(a.id, a.params)}
                                style={{ cursor: 'pointer' }}
                              />
                            ) : (
                              '—'
                            )}
                          </Box>
                        )}
                      />
                    </Table>
                  )
                }}
              </AutoSizer>
            </div>
            {(currentPage > 1 || !isLastPage) && (
              <TablePaginationControls
                currentPage={currentPage}
                setPage={setCurrentPage}
                isLastPage={isLastPage}
                startFromZero={false}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
